import { useEffect, useState } from "react";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { baseURL } from "../utils/common";

const CATEGORIES_URL = `${baseURL}/shop/admin/categories/`;
const CATEGORY_UPDATE_URL = (id) => `${baseURL}/shop/admin/category/${id}/update/`;
const SUB_CATEGORY_CREATE_URL = `${baseURL}/shop/admin/sub-category/create/`;
const SUB_CATEGORY_UPDATE_URL = (id) => `${baseURL}/shop/admin/sub-category/${id}/update/`;
const POSITIONS_UPDATE_URL = `${baseURL}/shop/admin/categories/positions/update/`;

const categoryFieldOrder = [
    "position",
    "ru_name",
    "slug",
    "name_ru",
    "name_en",
    "name_kk",
    "short_description_ru",
    "short_description_en",
    "short_description_kk",
];

const subCategoryFieldOrder = [
    "category_id",
    "image",
    "clear_image",
    "position",
    "ru_name",
    "slug",
    "name_ru",
    "name_en",
    "name_kk",
    "short_description_ru",
    "short_description_en",
    "short_description_kk",
];

const emptyCategoryFields = {
    position: { type: "number", label: "Позиция", required: false, default: 0 },
    ru_name: { type: "text", label: "Название для системы", required: true },
    slug: { type: "text", label: "Ссылка для перехода", required: true },
    name_ru: { type: "text", label: "Название RU", required: true },
    name_en: { type: "text", label: "Название EN", required: false },
    name_kk: { type: "text", label: "Название KK", required: false },
    short_description_ru: { type: "textarea", label: "Краткое описание RU", required: false },
    short_description_en: { type: "textarea", label: "Краткое описание EN", required: false },
    short_description_kk: { type: "textarea", label: "Краткое описание KK", required: false },
};

const getText = (value, lang = "ru") => {
    if (!value) return "—";
    if (typeof value === "string") return value || "—";

    return value[lang] || value.ru || "—";
};

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);

    result.splice(endIndex, 0, removed);

    return result.map((item, index) => ({
        ...item,
        position: index + 1,
    }));
};

const appendFormValue = (formData, key, value) => {
    if (value === undefined || value === null) return;

    if (typeof value === "object" && !(value instanceof File)) {
        formData.append(key, JSON.stringify(value));
        return;
    }

    formData.append(key, value);
};

const uploadFormData = (url, formData) => {
    return axios.post(url, formData, {
        transformRequest: [
            (data, headers) => {
                if (headers) {
                    delete headers["Content-Type"];
                    delete headers["content-type"];
                }

                return data;
            },
        ],
    });
};

export const ProductCategoriesPage = () => {
    const [categories, setCategories] = useState([]);
    const [categoryFields, setCategoryFields] = useState(emptyCategoryFields);
    const [expandedCategories, setExpandedCategories] = useState({});

    const [loading, setLoading] = useState(true);
    const [savingPositions, setSavingPositions] = useState(false);
    const [positionsChanged, setPositionsChanged] = useState(false);

    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("");
    const [modalTitle, setModalTitle] = useState("");
    const [modalFields, setModalFields] = useState({});
    const [modalForm, setModalForm] = useState({});
    const [modalFile, setModalFile] = useState(null);
    const [modalSaving, setModalSaving] = useState(false);
    const [modalError, setModalError] = useState("");
    const [editingId, setEditingId] = useState(null);

    const loadCategories = async () => {
        setLoading(true);
        setError("");
        setMessage("");

        try {
            const response = await axios.get(CATEGORIES_URL);
            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось загрузить категории");
                return;
            }

            setCategories(data.categories || []);
            setCategoryFields(data.fields || emptyCategoryFields);
            setPositionsChanged(false);
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setLoading(false);
        }
    };

    const makeInitialForm = (fields) => {
        const nextForm = {};

        Object.keys(fields || {}).forEach((key) => {
            const field = fields[key];

            if (field.type === "file") return;

            if (field.type === "checkbox") {
                nextForm[key] = Boolean(field.value || field.default || false);
                return;
            }

            nextForm[key] = field.value ?? field.default ?? "";
        });

        return nextForm;
    };

    const toggleCategoryDetails = (categoryId) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    };

    const openCreateCategory = () => {
        setModalMode("create-category");
        setModalTitle("Добавить категорию");
        setEditingId(null);
        setModalFields(categoryFields || emptyCategoryFields);
        setModalForm(makeInitialForm(categoryFields || emptyCategoryFields));
        setModalFile(null);
        setModalError("");
        setModalOpen(true);
    };

    const openEditCategory = async (categoryId) => {
        setModalMode("edit-category");
        setModalTitle("Редактировать категорию");
        setEditingId(categoryId);
        setModalFile(null);
        setModalError("");
        setModalFields({});
        setModalForm({});
        setModalOpen(true);

        try {
            const response = await axios.get(CATEGORY_UPDATE_URL(categoryId));
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось получить данные категории");
                return;
            }

            setModalFields(data.fields || {});
            setModalForm(makeInitialForm(data.fields || {}));
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        }
    };

    const openCreateSubCategory = async (categoryId) => {
        setModalMode("create-sub-category");
        setModalTitle("Добавить подкатегорию");
        setEditingId(null);
        setModalFile(null);
        setModalError("");
        setModalFields({});
        setModalForm({});
        setModalOpen(true);

        try {
            const response = await axios.get(SUB_CATEGORY_CREATE_URL);
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось получить поля подкатегории");
                return;
            }

            const fields = data.fields || {};
            const form = makeInitialForm(fields);

            form.category_id = categoryId;

            setModalFields(fields);
            setModalForm(form);
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        }
    };

    const openEditSubCategory = async (subCategoryId) => {
        setModalMode("edit-sub-category");
        setModalTitle("Редактировать подкатегорию");
        setEditingId(subCategoryId);
        setModalFile(null);
        setModalError("");
        setModalFields({});
        setModalForm({});
        setModalOpen(true);

        try {
            const response = await axios.get(SUB_CATEGORY_UPDATE_URL(subCategoryId));
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось получить данные подкатегории");
                return;
            }

            setModalFields(data.fields || {});
            setModalForm(makeInitialForm(data.fields || {}));
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalMode("");
        setModalTitle("");
        setModalFields({});
        setModalForm({});
        setModalFile(null);
        setModalError("");
        setEditingId(null);
    };

    const changeModalField = (key, value) => {
        setModalForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const submitModal = async (e) => {
        e.preventDefault();

        if (modalSaving) return;

        setModalSaving(true);
        setModalError("");
        setMessage("");

        try {
            let url = "";
            let payload = modalForm;
            let config = {};

            if (modalMode === "create-category") {
                url = CATEGORIES_URL;
            }

            if (modalMode === "edit-category") {
                url = CATEGORY_UPDATE_URL(editingId);
            }

            if (modalMode === "create-sub-category") {
                url = SUB_CATEGORY_CREATE_URL;
            }

            if (modalMode === "edit-sub-category") {
                url = SUB_CATEGORY_UPDATE_URL(editingId);
            }

            if (modalMode.includes("sub-category")) {
                const formData = new FormData();

                Object.keys(modalForm).forEach((key) => {
                    appendFormValue(formData, key, modalForm[key]);
                });

                if (modalFile) {
                    formData.append("image", modalFile);
                }

                payload = formData;
            }

            const response = payload instanceof FormData
                ? await uploadFormData(url, payload)
                : await axios.post(url, payload, config);
            const data = response.data;

            if (!data.success) {
                setModalError(data.message || "Не удалось сохранить данные");
                return;
            }

            setMessage(data.message || "Данные сохранены");
            closeModal();
            await loadCategories();
        } catch (e) {
            setModalError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setModalSaving(false);
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const { source, destination, type } = result;

        if (type === "CATEGORY") {
            if (source.index === destination.index) return;

            setCategories((prev) => reorder(prev, source.index, destination.index));
            setPositionsChanged(true);
            return;
        }

        if (type === "SUB_CATEGORY") {
            if (source.droppableId !== destination.droppableId) return;
            if (source.index === destination.index) return;

            const categoryId = Number(source.droppableId.replace("sub-list-", ""));

            setCategories((prev) =>
                prev.map((category) => {
                    if (category.id !== categoryId) return category;

                    return {
                        ...category,
                        sub_categories: reorder(
                            category.sub_categories || [],
                            source.index,
                            destination.index
                        ),
                    };
                })
            );

            setPositionsChanged(true);
        }
    };

    const savePositions = async () => {
        if (savingPositions) return;

        setSavingPositions(true);
        setError("");
        setMessage("");

        const categoriesPayload = categories.map((category, index) => ({
            id: category.id,
            position: index + 1,
        }));

        const subCategoriesPayload = [];

        categories.forEach((category) => {
            (category.sub_categories || []).forEach((subCategory, index) => {
                subCategoriesPayload.push({
                    id: subCategory.id,
                    position: index + 1,
                });
            });
        });

        try {
            const response = await axios.post(POSITIONS_UPDATE_URL, {
                categories: categoriesPayload,
                sub_categories: subCategoriesPayload,
            });

            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось сохранить позиции");
                return;
            }

            setMessage(data.message || "Позиции обновлены");
            setPositionsChanged(false);
            await loadCategories();
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setSavingPositions(false);
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    const renderField = (key) => {
        const field = modalFields[key];

        if (!field) return null;

        if (field.type === "file") {
            return (
                <label className="category-form-field" key={key}>
                    <span>{field.label}</span>

                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setModalFile(e.target.files?.[0] || null)}
                    />

                    {field.value && field.value !== "—" && (
                        <small>Текущая картинка: есть</small>
                    )}
                </label>
            );
        }

        if (field.type === "checkbox") {
            return (
                <label className="category-form-check" key={key}>
                    <input
                        type="checkbox"
                        checked={Boolean(modalForm[key])}
                        onChange={(e) => changeModalField(key, e.target.checked)}
                    />

                    <span>{field.label}</span>
                </label>
            );
        }

        if (field.type === "select") {
            return (
                <label className="category-form-field" key={key}>
                    <span>
                        {field.label}
                        {field.required && <b>*</b>}
                    </span>

                    <select
                        value={modalForm[key] || ""}
                        onChange={(e) => changeModalField(key, e.target.value)}
                    >
                        {(field.options || []).map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </label>
            );
        }

        if (field.type === "textarea") {
            return (
                <label className="category-form-field category-form-field-full" key={key}>
                    <span>
                        {field.label}
                        {field.required && <b>*</b>}
                    </span>

                    <textarea
                        value={modalForm[key] || ""}
                        onChange={(e) => changeModalField(key, e.target.value)}
                        placeholder={field.placeholder || ""}
                    />
                </label>
            );
        }

        return (
            <label className="category-form-field" key={key}>
                <span>
                    {field.label}
                    {field.required && <b>*</b>}
                </span>

                <input
                    type={field.type || "text"}
                    value={modalForm[key] || ""}
                    onChange={(e) => changeModalField(key, e.target.value)}
                    placeholder={field.placeholder || ""}
                />
            </label>
        );
    };

    return (
        <section className="product-categories-page">
            <div className="product-categories-head">
                <div>
                    <h1>Категории товаров</h1>
                    <p>Категории, подкатегории, картинки и порядок отображения</p>
                </div>

                <div className="product-categories-actions">
                    {positionsChanged && (
                        <button
                            className="categories-save-position-btn"
                            type="button"
                            onClick={savePositions}
                            disabled={savingPositions}
                        >
                            {savingPositions ? "Сохраняем..." : "Сохранить позиции"}
                        </button>
                    )}

                    <button type="button" onClick={openCreateCategory}>
                        + Категория
                    </button>
                </div>
            </div>

            {error && <div className="categories-error">{error}</div>}
            {message && <div className="categories-success">{message}</div>}

            {loading && <div className="categories-loading">Загрузка категорий...</div>}

            {!loading && categories.length === 0 && (
                <div className="categories-empty">Категорий пока нет</div>
            )}

            {!loading && categories.length > 0 && (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="categories-list" type="CATEGORY">
                        {(provided) => (
                            <div
                                className="categories-list"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {categories.map((category, categoryIndex) => {
                                    const isExpanded = Boolean(expandedCategories[category.id]);

                                    return (
                                        <Draggable
                                            key={`category-${category.id}`}
                                            draggableId={`category-${category.id}`}
                                            index={categoryIndex}
                                        >
                                            {(provided, snapshot) => (
                                                <article
                                                    className={
                                                        snapshot.isDragging
                                                            ? "category-card category-card-dragging"
                                                            : isExpanded
                                                                ? "category-card category-card-expanded"
                                                                : "category-card"
                                                    }
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                >
                                                    <div className="category-card-top">
                                                        <div
                                                            className="category-drag-handle"
                                                            {...provided.dragHandleProps}
                                                        >
                                                            ☰
                                                        </div>

                                                        <div className="category-card-info">
                                                            <span>Категория #{category.id}</span>
                                                            <h2>{category.ru_name || "—"}</h2>
                                                            <p>{getText(category.short_description)}</p>
                                                        </div>

                                                        <div className="category-card-meta">
                                                            <strong>Позиция {category.position}</strong>
                                                            <span>{category.sub_categories_count || 0} подкатегорий</span>
                                                        </div>

                                                        <button
                                                            className="category-details-toggle"
                                                            type="button"
                                                            onClick={() => toggleCategoryDetails(category.id)}
                                                        >
                                                            {isExpanded ? "Свернуть" : "Подробнее"}
                                                        </button>
                                                    </div>

                                                    {isExpanded && (
                                                        <div className="category-card-details">
                                                            <div className="category-card-grid">
                                                                <div>
                                                                    <span>Slug</span>
                                                                    <strong>{category.slug || "—"}</strong>
                                                                </div>

                                                                <div>
                                                                    <span>RU</span>
                                                                    <strong>{getText(category.name, "ru")}</strong>
                                                                </div>

                                                                <div>
                                                                    <span>EN</span>
                                                                    <strong>{getText(category.name, "en")}</strong>
                                                                </div>

                                                                <div>
                                                                    <span>KK</span>
                                                                    <strong>{getText(category.name, "kk")}</strong>
                                                                </div>
                                                            </div>

                                                            <div className="category-card-actions">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openEditCategory(category.id)}
                                                                >
                                                                    Изменить категорию
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => openCreateSubCategory(category.id)}
                                                                >
                                                                    + Подкатегория
                                                                </button>
                                                            </div>

                                                            <Droppable
                                                                droppableId={`sub-list-${category.id}`}
                                                                type="SUB_CATEGORY"
                                                            >
                                                                {(provided) => (
                                                                    <div
                                                                        className="sub-categories-list"
                                                                        ref={provided.innerRef}
                                                                        {...provided.droppableProps}
                                                                    >
                                                                        {(category.sub_categories || []).map((subCategory, subIndex) => (
                                                                            <Draggable
                                                                                key={`sub-category-${subCategory.id}`}
                                                                                draggableId={`sub-category-${subCategory.id}`}
                                                                                index={subIndex}
                                                                            >
                                                                                {(provided, snapshot) => (
                                                                                    <div
                                                                                        className={
                                                                                            snapshot.isDragging
                                                                                                ? "sub-category-card sub-category-card-dragging"
                                                                                                : "sub-category-card"
                                                                                        }
                                                                                        ref={provided.innerRef}
                                                                                        {...provided.draggableProps}
                                                                                    >
                                                                                        <div
                                                                                            className="sub-category-drag-handle"
                                                                                            {...provided.dragHandleProps}
                                                                                        >
                                                                                            ☰
                                                                                        </div>

                                                                                        <div className="sub-category-image">
                                                                                            {subCategory.image && subCategory.image !== "—" ? (
                                                                                                <img src={subCategory.image} alt="" />
                                                                                            ) : (
                                                                                                <span>IMG</span>
                                                                                            )}
                                                                                        </div>

                                                                                        <div className="sub-category-info">
                                                                                            <span>Подкатегория #{subCategory.id}</span>
                                                                                            <h3>{subCategory.ru_name || "—"}</h3>
                                                                                            <p>{getText(subCategory.short_description)}</p>
                                                                                        </div>

                                                                                        <div className="sub-category-meta">
                                                                                            <span>Позиция</span>
                                                                                            <strong>{subCategory.position}</strong>
                                                                                        </div>

                                                                                        <button
                                                                                            className="sub-category-edit"
                                                                                            type="button"
                                                                                            onClick={() => openEditSubCategory(subCategory.id)}
                                                                                        >
                                                                                            Изменить
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </Draggable>
                                                                        ))}

                                                                        {provided.placeholder}

                                                                        {(category.sub_categories || []).length === 0 && (
                                                                            <div className="sub-categories-empty">
                                                                                Подкатегорий пока нет
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </Droppable>
                                                        </div>
                                                    )}
                                                </article>
                                            )}
                                        </Draggable>
                                    );
                                })}

                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            {modalOpen && (
                <div className="category-modal">
                    <button
                        className="category-modal-bg"
                        type="button"
                        onClick={closeModal}
                    />

                    <div className="category-modal-card">
                        <div className="category-modal-head">
                            <div>
                                <h2>{modalTitle}</h2>
                                <p>Заполни данные для RU / EN / KK</p>
                            </div>

                            <button type="button" onClick={closeModal}>
                                ✕
                            </button>
                        </div>

                        {modalError && <div className="categories-error">{modalError}</div>}

                        <form className="category-form" onSubmit={submitModal}>
                            {(modalMode.includes("sub-category")
                                    ? subCategoryFieldOrder
                                    : categoryFieldOrder
                            ).map((key) => renderField(key))}

                            <div className="category-form-actions">
                                <button type="button" onClick={closeModal}>
                                    Отмена
                                </button>

                                <button type="submit" disabled={modalSaving}>
                                    {modalSaving ? "Сохраняем..." : "Сохранить"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </section>
    );
};
